<template>
  <div class="top-bar">
    <nav class="top-bar__navbar" :class="{'top-bar__navbar--visible': state.visible}">
      <section class="top-bar__section">
        <div class="top-bar__inner">
          <AppLogo class="top-bar__logo" />
          <button
            class="top-bar__button"
            :class="{
              'top-bar__button--no-opened': !isOpened
            }"
            @click.prevent="openMenu"
          >
            Menu
          </button>
        </div>
      </section>
    </nav>
    <div ref="menuRef">
      <LayoutsMainMenu :visible="isOpened" @close-menu="closeMenu" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive } from 'vue'
import { AppLogo } from '#components'
import useFocusTrap from '~/composables/useFocusTrap'

const menuRef = ref<HTMLElement | null>(null)
const isOpened = ref(false)

type TState = {
  visible: Boolean,
}

const state: TState = reactive({
  visible: false
})

onMounted(() => {
  state.visible = true
})

const openMenu = () => {
  isOpened.value = true
  document.body.classList.add('body--scroll-locked')
}

const closeMenu = () => {
  isOpened.value = false
  document.body.classList.remove('body--scroll-locked')
}

useFocusTrap(menuRef, isOpened)

</script>

<style scoped lang="postcss">
.top-bar {
  &__navbar {
    pointer-events: none;
    position: fixed;
    top: 0px;
    z-index: 40;
    margin-bottom: 2rem;
    width: 100%;
    opacity: 0;
    height: 60px;
    transform: translateY(-150%);
    transition: transform 250ms ease, opacity 125ms linear 75ms;

    &--visible {
      opacity: 1;
      transform: translateY(0);
    }

    &:before {
      position: absolute;
      display: block;
      width: 100%;
      content: '';
      height: 200%;
      background: linear-gradient(180deg, rgba(31, 31, 31, 0.85) -29.28%, rgba(31, 31, 31, 0) 82.57%);
      z-index: -1;
    }
  }

  &__section {
    position: relative;
    margin-top: 1.25rem;
    padding-left: 1.25rem;
    padding-right: 1.25rem;

    @media (--md) {
      padding-left: 40px;
      padding-right: 40px;
    }
  }

  &__inner {
    grid-column: 1 / -1;
    display: flex;
    justify-content: space-between;
  }

  &__logo {
    width: 52px;
    color: var(--color-blue-aqua);
    transition-duration: 300ms;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      color: #fff;
    }
  }

  &__button {
    pointer-events: none;
    font-family: Midnight, sans-serif;
    font-size: 0.875rem;
    text-transform: uppercase;
    line-height: 0.8;
    color: var(--color-blue-aqua);
    opacity: 0;
    transition-duration: 300ms;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    @mixin focus-ring;

    &:hover {
      color: #fff;
    }

    &--no-opened {
      pointer-events: auto;
      opacity: 1;
    }

    @media (--md) {
      font-size: 1rem;
      line-height: 1.5rem;
    }

    @media (--lg) {
      font-size: 1.125rem;
      line-height: 1.75rem;
    }
  }
}
</style>
